import React from 'react';
import propTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { Search, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from 'lib/utils';

const findInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    fontSize: '0.875rem'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--color-grey-lighter, #d0d0d1)'
  },
  '& .MuiInputBase-input': {
    py: '8px'
  }
};

const navButtonSx = {
  border: '1px solid var(--color-grey-lighter, #d0d0d1)'
};

/**
 * Cmd+F-style find bar for an agencies tree: a search field plus a match
 * counter and prev/next buttons. Pair it with useAgencyTreeFind, which owns
 * the matching and the scroll/highlight behavior.
 * @param searchQuery - current search text
 * @param onSearchChange - called with the new search text
 * @param onSearchKeyDown - key handler driving Enter and arrow navigation
 * @param searchTrimmed - the trimmed query, used to show the match counter
 * @param matchIndex - zero-based index of the active match
 * @param matchCount - total matches for the current query
 * @param onPrevMatch - selects the previous match
 * @param onNextMatch - selects the next match
 * @param placeholder - placeholder for the search field
 */
export default function AgencyTreeFindBar({
  searchQuery,
  onSearchChange,
  onSearchKeyDown,
  searchTrimmed,
  matchIndex,
  matchCount,
  onPrevMatch,
  onNextMatch,
  placeholder = 'Find agency by name...'
}) {
  return (
    <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3'>
      <div className='min-w-0 w-full sm:flex-1'>
        <TextField
          size='small'
          fullWidth
          placeholder={placeholder}
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          onKeyDown={onSearchKeyDown}
          aria-label='Find agency by name'
          sx={findInputSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start' sx={{ mr: 0.5 }}>
                <Search
                  className='size-[18px] text-grey shrink-0'
                  aria-hidden
                />
              </InputAdornment>
            )
          }}
        />
      </div>

      {searchTrimmed !== '' && (
        <div
          className={cn(
            'flex w-full items-center justify-between gap-3',
            'sm:w-auto sm:shrink-0 sm:justify-start sm:gap-2 sm:self-center'
          )}
          role='group'
          aria-label='Find matches'
        >
          <span className='text-sm tabular-nums text-grey' aria-live='polite'>
            {matchCount === 0
              ? 'No matches'
              : `${matchIndex + 1} / ${matchCount}`}
          </span>
          <div className='flex shrink-0 items-center gap-1'>
            <IconButton
              type='button'
              size='small'
              aria-label='Previous match'
              disabled={matchCount === 0}
              onClick={onPrevMatch}
              sx={navButtonSx}
            >
              <ChevronUp size={18} />
            </IconButton>
            <IconButton
              type='button'
              size='small'
              aria-label='Next match'
              disabled={matchCount === 0}
              onClick={onNextMatch}
              sx={navButtonSx}
            >
              <ChevronDown size={18} />
            </IconButton>
          </div>
        </div>
      )}
    </div>
  );
}

AgencyTreeFindBar.propTypes = {
  searchQuery: propTypes.string.isRequired,
  onSearchChange: propTypes.func.isRequired,
  onSearchKeyDown: propTypes.func.isRequired,
  searchTrimmed: propTypes.string.isRequired,
  matchIndex: propTypes.number.isRequired,
  matchCount: propTypes.number.isRequired,
  onPrevMatch: propTypes.func.isRequired,
  onNextMatch: propTypes.func.isRequired,
  placeholder: propTypes.string
};
